import { Component, Inject, Input, OnInit, ChangeDetectorRef } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AirIndicators, IModalAirQuality } from "@types-custom/models/ui/air_quality/air-quality-modal.model";
import { AirQualityModel } from "@shared/models/air_quality/AirQuality.model";
import { Icon } from "@types-custom/models/ui/icon-model";
import { ITimeLineGraphicModel } from "@types-custom/models/ui/chart-line-color-model";

@Component({
  selector: "sit-air-quality-modal",
  templateUrl: "./air-quality-modal.component.html",
  styleUrls: ["./air-quality-modal.component.scss"],
})
export class AirQualityModalComponent implements OnInit {
  @Input() properties: IModalAirQuality;
  public Indicators: AirIndicators[] = [];
  public filteredIndicators: AirIndicators[] = [];
  public ActiveIndicator: AirIndicators = null;
  public activeButton: 'air' | 'noise' | 'humidity' = 'air';

  lastModification: any;
  fullDate: any

  protected readonly Icon = Icon;


  public dataGraphicList: ITimeLineGraphicModel | null = null;
  public currentGraphDescription: string = '';
  public colorScale = [
    { minValue: 0, maxValue: 10, color: '#00FFC2' },
    { minValue: 10, maxValue: 20, color: '#00FFC2' },
    { minValue: 20, maxValue: 1000, color: '#00FFC2' },
    { minValue: 1000, maxValue: Number.MAX_SAFE_INTEGER, color: '#00FFC2' },
  ];

  public historicos: any[] = [];

  constructor(
    @Inject('environment') private environment: any,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    const valid_indicator_codes = new Set([
      'temp', //temperatura
      'hum', // humedad
      'prb', //presión atmosférica
      'co', //CO
      'no2', //NO2
    ]);

    this.properties.airQualityDataSource.getIndicatorsByStationId(this.properties.id).subscribe({
      // get data indicators modal  : filters to remove "stat" indicator
      next: (res: any) => {
        this.Indicators = (this.environment.citizen ?? false) ? res.indicators.filter((ind: any) => valid_indicator_codes.has(ind.indicatorCode)) : res.indicators.filter((ind:any)=> ind.indicatorCode != 'stat' )
        this.filterIndicators();
        // this.getMeasurements(+this.properties.id) //TODO, there is no graphic data available at the moment
        this.fullDate = res.lastModification.split(/[TZ]/).join(" ")
      }
    });

    // Cargar datos históricos del endpoint
    this.loadHistoricalDataFromAPI();
  }

  private loadHistoricalDataFromAPI(): void {
    const stationId = this.properties.id;
    const apiUrl = `https://gateway.sitbogota.com/environmental-analytics/api/v1/station/${stationId}/average-measurements`;
    
    this.http.get<any[]>(apiUrl).subscribe({
      next: (response: any[]) => {
        this.historicos = response;
      },
      error: (error: any) => {
        console.error('Error loading historical data from API:', error);
      }
    });
  }

  public ChangeIndicator($event: AirIndicators) {
    this.ActiveIndicator = $event;
    this.loadHistoricalData();
  }

  private loadHistoricalData(): void {
    if (!this.ActiveIndicator) {
      this.dataGraphicList = null;
      return;
    }

    // Mapear indicatorCode al name en historicos
    const codeToNameMap: { [key: string]: string } = {
      'Ruido': 'noise',
      'Humedad': 'hum',
      'co': 'co',
      'CO': 'co',
      'no2': 'no2',
      'NO2': 'no2',
      'o3': 'o3',
      'O3': 'o3',
      'pm1': 'pm1',
      'PM1': 'pm1',
      'pm10': 'pm10',
      'PM10': 'pm10',
      'pm25': 'pm25',
      'PM25': 'pm25',
      'PM2.5': 'pm25',
      'so2': 'so2',
      'SO2': 'so2',
      'temp': 'temp',
      'TEMP': 'temp',
      'temperature': 'temp'
    };

    const indicatorCode = this.ActiveIndicator.indicatorCode || this.ActiveIndicator.indicatorName || '';
    const indicatorName = codeToNameMap[indicatorCode] || indicatorCode.toLowerCase();
        
    // Buscar el histórico correspondiente
    const historico = this.historicos.find(h => h.name === indicatorName || h.name === indicatorCode.toLowerCase());
        
    if (historico && historico.graph && historico.graph.length > 0) {
      this.currentGraphDescription = historico.description || '';
      
      const dataGraphic = historico.graph.map((item: any) => {      
        const date = new Date(item.date);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const time = `${hours}:${minutes}`;
        
        return {
          time: time,
          value: item.value,
        };
      });

      this.dataGraphicList = {
        data: dataGraphic,
        labelX: 'Hora',
      };
      
    } else {
      // Si no se encuentra, limpiar el gráfico
      this.dataGraphicList = null;
      this.currentGraphDescription = '';
    }
  }

  public getMeasurements(stationId: number) {
    this.properties.airQualityDataSource.getMeasurementsByStationId(stationId).subscribe((res: any) => {
      this.setGraphicData(res.data);
    })
  }


  private setGraphicData(info: any): void {
    const dataGraphic = info
      .map((graph: any) => {
        return {
          time: graph.time,
          value: graph.value,
        };
      })
      .sort((x: any, y: any) => x.time - y.time);

    this.dataGraphicList = {
      data: dataGraphic,
      labelX: 'Hora',
    };
  }

  public formatDate(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  }

  public selectButton(buttonType: 'air' | 'noise' | 'humidity'): void {
    this.activeButton = buttonType;
    this.filterIndicators();
  }

  private filterIndicators(): void {
    if (this.activeButton === 'noise') {
      this.filteredIndicators = this.Indicators.filter(ind => ind.indicatorCode === 'Ruido');
    } else if (this.activeButton === 'humidity') {
      this.filteredIndicators = this.Indicators.filter(ind => ind.indicatorCode === 'Humedad' || ind.indicatorCode === 'temp');
    } else {
      this.filteredIndicators = this.Indicators.filter(ind => ind.indicatorCode !== 'Ruido' && ind.indicatorCode !== 'Humedad' && ind.indicatorCode !== 'temp');
    }
    
    this.ActiveIndicator = this.filteredIndicators.length > 0 ? this.filteredIndicators[0] : null;
    
    this.cdr.detectChanges();
    
    // Cargar los datos históricos del primer indicador automáticamente
    if (this.ActiveIndicator) {
      this.loadHistoricalData();
      this.cdr.detectChanges();
      
      setTimeout(() => {
        this.loadHistoricalData();
        this.cdr.detectChanges();
      }, 200);
    } else {
      this.dataGraphicList = null;
    }
  }
}
